/* ============================================================
   PRODUCT FORM — Variant selection & add-to-cart
   ============================================================ */
(function() {
  'use strict';

  const ProductForm = {
    init() {
      this.form = document.querySelector('[data-product-form]');
      if (!this.form) return;

      this.productData = this.getProductJSON();
      if (!this.productData) return;

      this.variantSelector = this.form.querySelector('[data-variant-select]');
      this.variantOptions = this.form.querySelectorAll('[data-variant-option]');
      this.addToCartBtn = this.form.querySelector('[data-add-to-cart]');
      this.addToCartText = this.form.querySelector('[data-add-to-cart-text]');
      this.priceEl = document.querySelector('[data-product-price]');
      this.comparePriceEl = document.querySelector('[data-product-compare-price]');
      this.savePriceEl = document.querySelector('[data-product-save-price]');

      this.bindEvents();
      this.updateVariant();
    },

    getProductJSON() {
      const el = document.querySelector('[data-product-json]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
        return null;
      }
    },

    bindEvents() {
      // Swatch/button variant selection
      this.variantOptions.forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          const group = option.closest('[data-option-group]');
          group?.querySelectorAll('[data-variant-option]').forEach(o => {
            o.classList.remove('is-selected');
          });
          option.classList.add('is-selected');
          this.updateVariant();
        });
      });

      // Dropdown variant selection
      this.variantSelector?.addEventListener('change', () => {
        this.updateVariant();
      });

      // Add to cart
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    },

    getSelectedOptions() {
      const options = [];
      this.form.querySelectorAll('[data-option-group]').forEach(group => {
        const selected = group.querySelector('[data-variant-option].is-selected');
        if (selected) {
          options.push(selected.dataset.variantOption);
        }
      });
      return options;
    },

    findVariant(options) {
      return this.productData.variants.find(variant => {
        return options.every((option, index) => {
          return variant.options[index] === option;
        });
      });
    },

    updateVariant() {
      let variant;

      // Prefer button-based selection when option groups exist,
      // fall back to the hidden <select> value otherwise
      const options = this.getSelectedOptions();
      if (options.length > 0) {
        variant = this.findVariant(options);
      } else if (this.variantSelector) {
        const variantId = this.variantSelector.value;
        variant = this.productData.variants.find(v => v.id.toString() === variantId);
      }

      if (!variant) {
        this.setUnavailable();
        return;
      }

      // Sync the hidden <select> so the correct variant ID is submitted
      if (this.variantSelector) {
        this.variantSelector.value = variant.id;
      }

      this.updateOptionLabels();
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateURL(variant);
      this.updateGallery(variant);
    },

    updatePrice(variant) {
      const formatMoney = window.Utils?.formatMoney;
      if (!formatMoney) return;

      if (this.priceEl) {
        this.priceEl.textContent = formatMoney(variant.price);
      }

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (this.comparePriceEl) {
          this.comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          this.comparePriceEl.classList.remove('hidden');
        }
        if (this.savePriceEl) {
          const savings = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          this.savePriceEl.textContent = `Save ${savings}%`;
          this.savePriceEl.classList.remove('hidden');
        }
      } else {
        this.comparePriceEl?.classList.add('hidden');
        this.savePriceEl?.classList.add('hidden');
      }

      // Update sticky ATC price on mobile
      const stickyPrice = document.querySelector('[data-sticky-price]');
      if (stickyPrice) {
        stickyPrice.textContent = formatMoney(variant.price);
      }
    },

    updateButton(variant) {
      if (!this.addToCartBtn || !this.addToCartText) return;

      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartText.textContent = 'Add to Basket';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartText.textContent = 'Sold Out';
      }
    },

    updateURL(variant) {
      if (!variant || !history.replaceState) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    },

    /**
     * Update the selected option label text (e.g. "Colour: Chrome")
     * so the shopper always sees which value is active.
     */
    updateOptionLabels() {
      this.form.querySelectorAll('[data-option-group]').forEach(group => {
        const selected = group.querySelector('[data-variant-option].is-selected');
        const label = group.querySelector('[data-option-selected]');
        if (selected && label) {
          label.textContent = selected.dataset.variantOption;
        }
      });
    },

    /**
     * Navigate the product gallery to the variant's featured image.
     * Uses ProductGallery.goToImageId() for full gallery sync
     * (main image, active thumbnail, counter, scroll position).
     */
    updateGallery(variant) {
      if (!variant.featured_image) return;

      // Use the ProductGallery module for proper gallery navigation
      const gallery = window.ProductGallery;
      if (gallery && typeof gallery.goToImageId === 'function') {
        gallery.goToImageId(variant.featured_image.id);
        return;
      }

      // Fallback: update main image src directly if gallery not available
      const mainImage = document.querySelector('[data-product-main-image]');
      if (mainImage) {
        mainImage.src = variant.featured_image.src;
      }
    },

    setUnavailable() {
      if (this.addToCartBtn) this.addToCartBtn.disabled = true;
      if (this.addToCartText) this.addToCartText.textContent = 'Unavailable';
    },

    async addToCart() {
      const variant = this.getCurrentVariant();
      if (!variant || !variant.available) return;

      const qtyInput = this.form.querySelector('[data-qty-input]');
      const quantity = parseInt(qtyInput?.value) || 1;

      this.addToCartBtn.disabled = true;
      this.addToCartText.textContent = 'Adding...';

      try {
        await window.CartDrawer?.addItem(variant.id, quantity);
        this.addToCartText.textContent = 'Added to Basket';

        setTimeout(() => {
          this.addToCartText.textContent = 'Add to Basket';
          this.addToCartBtn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.addToCartText.textContent = 'Add to Basket';
        this.addToCartBtn.disabled = false;
      }
    },

    getCurrentVariant() {
      if (this.variantSelector) {
        const variantId = this.variantSelector.value;
        return this.productData.variants.find(v => v.id.toString() === variantId);
      }
      const options = this.getSelectedOptions();
      return this.findVariant(options);
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    ProductForm.init();
  });
})();
